import { IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CounterPartySelectorContext from './CounterPartySelectorContext';
import { useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CounterPartyFilterDialog from './CounterPartyFilterDialog';
import { counterPartyModel, CounterPartyType, portModel } from 'model';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import { TXT_CLEAR, TXT_COUNTERPARTY } from '../../../../../shared/translations';

type DataType = Partial<counterPartyModel>;

interface CounterPartySelectorProps {
  defaultValue?: DataType;
  counterPartyType: CounterPartyType;
  port?: Partial<portModel>;
  onChange: (counterParty?: DataType) => void;
  label?: string;
  error?: boolean;
  allowAdd?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  disablePortFilter?: boolean;
  disableCountryFilter?: boolean;
}

const CounterPartySelectorV2 = (props: CounterPartySelectorProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<DataType | undefined>(props.defaultValue);
  const [showFilter, setShowFilter] = useState(false);

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const onSelectedChanged = (counterParty?: DataType) => {
    setSelected(counterParty);
  };

  const clear = () => {
    setSelected(undefined);
  };

  useEffect(() => {
    props.onChange(selected);
  }, [selected]);

  return (
    <CounterPartySelectorContext.Provider
      value={{
        counterParty: selected,
        counterPartyType: props.counterPartyType,
        port: props.port,
        allowAdd: props.allowAdd,
        onChange: onSelectedChanged,
        disablePortFilter: props.disablePortFilter,
        disableCountryFilter: props.disableCountryFilter
      }}
    >
      {showFilter ? <CounterPartyFilterDialog onClose={toggleFilter} /> : null}
      <TextField
        fullWidth
        label={props.label || capitalizeEveryWord(t(TXT_COUNTERPARTY))}
        value={selected?.name || ''}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row" spacing={1} alignItems="center">
                {props.allowClear && selected && !props.disabled ? (
                  <Tooltip title={capitalizeEveryWord(t(TXT_CLEAR))}>
                    <ClearIcon
                      fontSize="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        clear();
                      }}
                      color="error"
                      sx={{
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                ) : null}
                <IconButton
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleFilter();
                  }}
                  disabled={props.disabled}
                  size="small"
                >
                  <ArrowDropDownIcon />
                </IconButton>
              </Stack>
            </InputAdornment>
          )
        }}
        onClick={toggleFilter}
        disabled={props.disabled}
        error={props.error}
      />
    </CounterPartySelectorContext.Provider>
  );
};

export default CounterPartySelectorV2;
